import { useEffect, useMemo, useState } from "react";
import styles from "./TimeTracking.module.css";
import { useAuth0 } from "@auth0/auth0-react";
import axios from "axios";
import WeekViewTable from "./WeekViewTable";
import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import Filter from "./Filter";
import DayViewTable from "./DayViewTable";
import Loader from "../../../components/Loader";
import { toast } from 'react-smart-toaster';
import { AddLogCallAPI } from "../../../components/AddLogs";
import { endOfPayWeek, startOfPayWeek } from "../../../utils/payWeek";
import { calcDayWage, calcWeeklyPay, formatMinutesToHM, isWeekend, workdayIndex } from "./weeklyPay";
import { DEFAULT_CURRENCY, formatCurrencyDecimal } from "../../../helpers/currency";
import UserDetails from "../../users/components/UserDetails";

dayjs.extend(isoWeek);

const WAGE_CURRENCIES = ['USD', 'INR'];

const ChevronLeft = () => (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M15 18l-6-6 6-6" /></svg>);
const ChevronRight = () => (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M9 18l6-6-6-6" /></svg>);

const WageCard = ({ title, wages, subFor }) => (
    <div className={`${styles.scard} ${styles.scardWide}`}>
        {WAGE_CURRENCIES.map((code, i) => (<div key={code} className={`${styles.wageSplit}`}>
            {i > 0 && <div className={`${styles.wageDivider}`} />}
            <div className={`${styles.wageBlock}`}>
                <div className={`${styles.scardLbl}`}>{title} · {code}</div>
                <div className={`${styles.scardVal}`}>{formatCurrencyDecimal(wages?.[code]?.total || 0, code)}</div>
                <div className={`${styles.scardSub}`}>{subFor(code)}</div>
            </div>
        </div>))}
    </div>
);

export default function TimeTracking() {
    const { getAccessTokenSilently } = useAuth0();
    const [loading, setLoading] = useState(false);
    const [contentLoading, setContentLoading] = useState(true);
    const [allData, setAllData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [selectedDate, setSelectedDate] = useState(dayjs().format('YYYY-MM-DD'));
    const [weekOffset, setWeekOffset] = useState(0);
    const [dateRange, setDateRange] = useState({ start: startOfPayWeek(dayjs()).format('YYYY-MM-DD'), end: endOfPayWeek(dayjs()).format('YYYY-MM-DD') });
    const [dateRangeText, setDateRangeText] = useState('');
    const [filterData, setFilterData] = useState({ property: '0', role: '' });
    const [activeView, setActiveView] = useState("Week");
    const [detailsUser, setDetailsUser] = useState(null);

    useEffect(() => {
        const getUserList = async () => {
            setContentLoading(true);

            const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });

            axios(process.env.REACT_APP_USER_API_URL + "v1/time-tracking-list", {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                params: { start_date: dateRange?.start, end_date: dateRange?.end }
            }).then((response) => {
                setContentLoading(false);
                setAllData(response.data.data);
            }).catch(() => {
                setContentLoading(false);
            });
        }

        getUserList();
    }, [getAccessTokenSilently, dateRange]);

    // Both views load the whole Fri–Thu pay week: the day view needs the week to date.
    useEffect(() => {
        const refDate = activeView === 'Day' ? dayjs(selectedDate) : dayjs().add(weekOffset, "week");
        const start = startOfPayWeek(refDate).format('YYYY-MM-DD');
        const end = endOfPayWeek(refDate).format('YYYY-MM-DD');
        setDateRange(prev => (prev?.start === start && prev?.end === end) ? prev : { start, end });
    }, [weekOffset, selectedDate, activeView]);

    useEffect(() => {
        const start = dayjs(dateRange?.start);
        const end = dayjs(dateRange?.end);
        if (start.format('YYYY') !== end.format('YYYY')) {
            setDateRangeText(start.format('MMM D, YYYY') + ' – ' + end.format('MMM D, YYYY'));
        } else if (start.format('MMM') !== end.format('MMM')) {
            setDateRangeText(start.format('MMM D') + ' – ' + end.format('MMM D, YYYY'));
        } else {
            setDateRangeText(start.format('MMM D') + ' – ' + end.format('D, YYYY'));
        }
    }, [dateRange]);

    useEffect(() => {
        let filteredDataTemp = [...allData];

        if (parseInt(filterData?.property) > 0) {
            filteredDataTemp = filteredDataTemp?.filter(i => i?.property_ids.includes(parseInt(filterData?.property)));
        }

        if (filterData?.role !== '' && filterData?.role !== 'all') {
            filteredDataTemp = filteredDataTemp?.filter(i => i?.role_id === filterData?.role);
        }

        setFilteredData(filteredDataTemp);
    }, [allData, filterData]);

    const widgetData = useMemo(() => {
        let regular = 0;
        let ot = 0;
        let weekendOt = 0;
        let weeklyOt = 0;
        let late = 0;
        let missed = 0;
        const wages = {};

        const addWage = (currency, regularWage, otWage) => {
            if (!wages[currency]) wages[currency] = { total: 0, regular: 0, ot: 0 };
            wages[currency].regular += regularWage;
            wages[currency].ot += otWage;
            wages[currency].total += regularWage + otWage;
        };

        filteredData?.forEach(emp => {
            const currency = emp?.currency || DEFAULT_CURRENCY;
            const pay = calcWeeklyPay(emp);
            const entries = activeView === 'Week' ? (emp?.timeTracking || []) : (emp?.timeTracking || []).filter(t => t?.date === selectedDate);

            entries.forEach(t => {
                if (t?.checkin_time && t?.late) late += 1;
                if (t?.checkin_time && !t?.checkout_time) missed += 1;
            });

            if (activeView === 'Week') {
                regular += pay.regularMinutes;
                ot += pay.otMinutes;
                weekendOt += pay.weekendOtMinutes;
                weeklyOt += pay.weeklyOtMinutes;
                addWage(currency, pay.regularWage, pay.otWage);
            } else {
                const day = pay.days[selectedDate];
                if (!day) return;
                regular += day.reg;
                ot += day.ot;
                const dayWage = calcDayWage(emp, day);
                addWage(currency, dayWage.regularWage, dayWage.otWage);
            }
        });

        return { regular, ot, weekendOt, weeklyOt, late, missed, wages };
    }, [filteredData, activeView, selectedDate]);

    const updateTimeTracking = (data) => {
        setAllData(prev => {
            return prev?.map(i => {
                if (i?.id === data?.user_id) {
                    let timeTracking = i?.timeTracking?.map(si => {
                        if (si?.date === data?.date) {
                            return { ...si, approved_ot_time: data?.approved_ot_time, break_mintes: data?.break_mintes, break_times: data?.break_times, checkin_time: data?.checkin_time, checkout_time: data?.checkout_time, checkout_date: data?.checkout_date, is_overnight: data?.is_overnight, created_at: data?.created_at, date: data?.date, day: data?.day, id: data?.id, is_ot_approved: data?.is_ot_approved, late: data?.late, lunch_end_time: data?.lunch_end_time, lunch_start_time: data?.lunch_start_time, ot_time: data?.ot_time, regular_minutes: data?.regular_minutes, shift_minutes: data?.shift_minutes, updated_at: data?.updated_at, user_id: data?.user_id, user_name: data?.user_name, working_time: data?.working_time };
                        }
                        return si;
                    });

                    return { ...i, timeTracking: timeTracking };
                }
                return i;
            })
        })
    }

    const deleteTimeTracking = async (itemId) => {
        setLoading(true);

        const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });

        axios.delete(process.env.REACT_APP_USER_API_URL + 'v1/time-tracking/delete-time/' + itemId, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        }).then(response => {
            setLoading(false);

            let item = response.data.data;

            setAllData(prev => {
                return prev.map(i => {
                    if (i?.id === item?.user_id) {
                        let timeTracking = i?.timeTracking?.map(t => {
                            if (t?.date === item?.date) {
                                return { ...t, day_off: 1, approved_ot_time: 0, break_mintes: 0, checkin_time: null, checkout_time: null, created_at: null, is_ot_approved: 0, late: "", ot_time: 0, regular_minutes: 0, shift_minutes: 0, updated_at: null, user_id: 0, working_time: 0 };
                            }
                            return t;
                        });

                        return { ...i, timeTracking: timeTracking };
                    }

                    return i;
                });
            });

            let logData = {
                'title': 'Track time has been deleted successfully.',
                'description': [
                    'Employee name: ' + item?.user_name,
                    'Date: ' + dayjs(item?.date).format('MM/DD/YYYY'),
                ]
            }
            AddLogCallAPI(logData, token);
        }).catch(error => {
            if (typeof error.response !== 'undefined')
                toast.error(error.response.data.message);
            setLoading(false);
        });
    }

    const goPrev = () => { setWeekOffset(e => e - 1); setSelectedDate(dayjs(selectedDate).subtract(1, 'day').format('YYYY-MM-DD')); };
    const goNext = () => { setWeekOffset(e => e + 1); setSelectedDate(dayjs(selectedDate).add(1, 'day').format('YYYY-MM-DD')); };

    const dayIsWeekend = isWeekend(selectedDate);
    const dayIsFriday = dayjs(selectedDate).day() === 5;
    const wageSub = (code) => `Reg ${formatCurrencyDecimal(widgetData.wages?.[code]?.regular || 0, code)} · OT ${formatCurrencyDecimal(widgetData.wages?.[code]?.ot || 0, code)}`;
    const otherWages = Object.entries(widgetData.wages).filter(([code]) => !WAGE_CURRENCIES.includes(code));

    const lateCard = (<div className={`${styles.scard}`}>
        <div className={`${styles.scardLbl}`}>Late clock-ins</div>
        <div className={`${styles.scardVal} ${styles.textLate}`}>{widgetData.late}</div>
        <div className={`${styles.scardSub}`}>Missed clock-outs: {widgetData.missed}</div>
    </div>);

    return (<>
        {loading && <Loader />}
        <div className={`${styles.page}`}>

            {/* ── Topbar ── */}
            <div className={`${styles.topbar}`}>
                <div>
                    <div className={`${styles.pageEyebrow}`}>HR &amp; Workforce</div>
                    <h1 className={`${styles.pageTitle}`}>Time Tracker</h1>
                </div>
                <div className={`${styles.topbarRight}`}>
                    <div className={`${styles.viewTabs}`}>
                        {["Day", "Week"].map(v => (<button key={v} className={`${styles.vtab} ${activeView === v ? styles.vtabOn : ""}`} onClick={() => { setActiveView(v); setSelectedDate(dayjs().format('YYYY-MM-DD')); setWeekOffset(0); }}>{v}</button>))}
                    </div>
                    <div className={`${styles.weekNav}`}>
                        <button className={`${styles.navBtn}`} aria-label={activeView === "Day" ? "Previous day" : "Previous week"} onClick={goPrev}><ChevronLeft /></button>
                        <span className={`${styles.weekLbl}`}>{activeView === "Day" ? dayjs(selectedDate).format('MMM D, YYYY') : dateRangeText}</span>
                        <button className={`${styles.navBtn}`} aria-label={activeView === "Day" ? "Next day" : "Next week"} onClick={goNext}><ChevronRight /></button>
                    </div>
                </div>
            </div>

            {/* ── Stats ── */}
            <div className={`${styles.stats}`}>
                {activeView === "Week" && (<>
                    <div className={`${styles.scard}`}>
                        <div className={`${styles.scardLbl}`}>Total hours</div>
                        <div className={`${styles.scardVal}`}>{formatMinutesToHM(widgetData.regular + widgetData.ot, '0h')}</div>
                        <div className={`${styles.scardSub}`}>Reg {formatMinutesToHM(widgetData.regular, '0h')} · OT {formatMinutesToHM(widgetData.ot, '0h')}</div>
                    </div>
                    <div className={`${styles.scard}`}>
                        <div className={`${styles.scardLbl}`}>Overtime</div>
                        <div className={`${styles.scardVal} ${styles.textOt}`}>{formatMinutesToHM(widgetData.ot, '0h')}</div>
                        <div className={`${styles.scardSub}`}>Weekend {formatMinutesToHM(widgetData.weekendOt, '0h')} · Weekly {formatMinutesToHM(widgetData.weeklyOt, '0h')}</div>
                    </div>
                    <WageCard title="Total wages" wages={widgetData.wages} subFor={wageSub} />
                    {lateCard}
                </>)}

                {activeView === "Day" && !dayIsWeekend && (<>
                    <div className={`${styles.scard}`}>
                        <div className={`${styles.scardLbl}`}>Regular hours today</div>
                        <div className={`${styles.scardVal}`}>{formatMinutesToHM(widgetData.regular, '0h')}</div>
                        <div className={`${styles.scardSub}`}>Weekday · all time is Regular</div>
                    </div>
                    {widgetData.ot > 0 && <div className={`${styles.scard}`}>
                        <div className={`${styles.scardLbl}`}>Weekly OT today</div>
                        <div className={`${styles.scardVal} ${styles.textOt}`}>{formatMinutesToHM(widgetData.ot)}</div>
                        <div className={`${styles.scardSub}`}>Weekday hours past 40h</div>
                    </div>}
                    <WageCard title="Wages today" wages={widgetData.wages} subFor={wageSub} />
                    {lateCard}
                </>)}

                {activeView === "Day" && dayIsWeekend && (<>
                    <div className={`${styles.scard}`}>
                        <div className={`${styles.scardLbl}`}>Overtime today</div>
                        <div className={`${styles.scardVal} ${styles.textOt}`}>{formatMinutesToHM(widgetData.ot, '0h')}</div>
                        <div className={`${styles.scardSub}`}>Weekend · all time is OT</div>
                    </div>
                    <div className={`${styles.scard}`}>
                        <div className={`${styles.scardLbl}`}>Regular hours today</div>
                        <div className={`${styles.scardVal} ${styles.textMuted}`}>—</div>
                        <div className={`${styles.scardSub}`}>Weekend adds no regular time</div>
                    </div>
                    <WageCard title="Wages today" wages={widgetData.wages} subFor={() => 'OT rate'} />
                    {lateCard}
                </>)}
            </div>

            {otherWages.length > 0 && (<div className={`${styles.scardSub}`} style={{ marginTop: -8, marginBottom: 16 }}>
                Other currencies: {otherWages.map(([code, amt]) => formatCurrencyDecimal(amt?.total || 0, code)).join(' · ')}
            </div>)}

            <div className={`${styles.tblHeader}`}>
                {activeView === "Week" && <h2 className={`${styles.tblTitle}`}>Weekly timesheets</h2>}
                {activeView === "Day" && <h2 className={`${styles.dvDayTitle}`}>
                    {dayjs(selectedDate).format('dddd, MMM D, YYYY')}
                    {dayIsWeekend
                        ? <span className={`${styles.dayBadge} ${styles.dayBadgeOt}`}>WEEKEND · ALL OT</span>
                        : <span className={`${styles.dayBadge}`}>{dayIsFriday ? 'WEEK START · WORKDAY 1 OF 5' : `WEEKDAY · WORKDAY ${workdayIndex(selectedDate)} OF 5`}</span>}
                </h2>}

                <Filter setFilterData={setFilterData} />
            </div>

            {/* ══ WEEK VIEW ══ */}
            {activeView === "Week" && (
                <>
                    <WeekViewTable contentLoading={contentLoading} filteredData={filteredData} dateRange={dateRange} setLoading={setLoading} updateTimeTracking={updateTimeTracking} deleteTimeTracking={deleteTimeTracking} onOpenEmployee={setDetailsUser} />

                    <div className={`${styles.legend}`}>
                        <span className={`${styles.legItem}`}><span className={`${styles.legSwatch} ${styles.dayReg}`} />Regular (weekday)</span>
                        <span className={`${styles.legItem}`}><span className={`${styles.legSwatch} ${styles.dayOt}`} />Overtime (weekend)</span>
                        <span className={`${styles.legItem}`}><span className={`${styles.legSwatch} ${styles.dayLate}`} />Late clock-in</span>
                        <span className={`${styles.legItem}`}>Weekly OT = weekday hours past 40h, settled after Thu (5th workday of the Fri–Thu week)</span>
                    </div>
                </>
            )}

            {activeView === "Day" && (
                <>
                    <DayViewTable contentLoading={contentLoading} filteredData={filteredData} selectedDate={selectedDate} setLoading={setLoading} updateTimeTracking={updateTimeTracking} deleteTimeTracking={deleteTimeTracking} onOpenEmployee={setDetailsUser} />

                    <div className={`${styles.legend}`}>
                        {dayIsWeekend ? (<>
                            <span className={`${styles.legItem}`}>Saturday and Sunday: 100% of time worked is OT.</span>
                            <span className={`${styles.legItem}`}>Weekend hours don’t count toward the 40h weekday threshold.</span>
                        </>) : (<>
                            {dayIsFriday && <span className={`${styles.legItem}`}>New Fri–Thu week starts today — week to date resets to 0.</span>}
                            <span className={`${styles.legItem}`}>Weekday logs show Regular time only — no daily OT.</span>
                        </>)}
                    </div>
                </>
            )}
        </div>

        {/* Employee details (read only) */}
        <div className={detailsUser ? `${styles.detailsPanel} ${styles.detailsPanelOpen}` : `${styles.detailsPanel}`} onClick={() => setDetailsUser(null)}>
            <div className={`${styles.detailsPanelInner}`} onClick={(e) => e.stopPropagation()}>
                <UserDetails readOnly selectedUser={detailsUser} CloseTaskDetails={() => setDetailsUser(null)} />
            </div>
        </div>
    </>);
}
